import Axios from 'axios';
import api from '@ts/api';
import routes from '@ts/containers/App/routes';

const chunks = {};

export default class PrefetchObject {
    _key = '';
    _loadable = null;
    _progress = { loaded: 0, total: 0 };
    _result = null;
    _isLoading = false;
    _isLoaded = false;
    _dataSource = null;
    _onLoad = null;
    _onProgress = null;

    constructor(key, loadable) {
        this._key = key;
        this._loadable = loadable;
    }

    get isLoading() {
        return this._isLoading;
    }

    get isLoaded() {
        return this._isLoaded;
    }

    get progress() {
        return this._progress;
    }

    get result() {
        return this._result;
    }

    get loadable() {
        return this._loadable;
    }

    get key() {
        return this._key;
    }

    downloadProgress = (ProgressEvent) => {
        this._progress.total = ProgressEvent.total;
        this._progress.loaded = ProgressEvent.loaded;
        if (this._onProgress) {
            this._onProgress(this);
        }
        return this;
    };

    loader = async () => {
        switch (this._loadable.path) {
            case routes.activityPage:
            case routes.activityReviewsPage:
            case routes.activityReviewPage:
                return api.activities.fetchById(
                    this._loadable.params.id,
                    { is_full: 1 },
                    {
                        cancelToken: this._dataSource.token,
                        onDownloadProgress: (ProgressEvent) => this.downloadProgress(ProgressEvent),
                    }
                );
            case routes.blogPostPage:
                return api.blogs.fetchBySlug(this._loadable.params.slug);
            case routes.storyPage:
                return api.stories.fetchBySlug(this._loadable.params.slug);
            case routes.contentPage:
                return api.pages.fetchBySlug(this._loadable.params.slug);
            default:
                throw new Error('Unknown Loader.');
        }
    };

    LoaderImagesOnActivityPage = async () => {
        switch (this._loadable.path) {
            case routes.activityPage:
                return api.activities.fetchUserImagesById(this._loadable.params.id);
            case routes.storyPage:
                return api.stories.fetchUserImagesBySlug(this._loadable.params.slug);
            default:
                return;
        }
    };

    chunkLoader = async () => {
        try {
            if (
                chunks[this._loadable.path] &&
                typeof chunks[this._loadable.path].preload === 'function'
            ) {
                return chunks[this._loadable.path].preload();
            }
        } catch {}
    };

    fetch = async () => {
        try {
            this.cancel();
            this._dataSource = api.CancelToken.source();
            this._isLoading = true;
            this.downloadProgress({ total: 1000, loaded: 700 });

            this._result = await Promise.all([this.loader(), this.LoaderImagesOnActivityPage()]);
            await this.chunkLoader();

            this._isLoaded = true;
            this._isLoading = false;
            this._dataSource = null;

            this.downloadProgress({
                total: 1000,
                loaded: 1000,
            });

            if (this._onLoad) {
                this._onLoad(this);
            }

            return this;
        } catch (err) {
            if (!Axios.isCancel(err)) {
                console.error(err);
                throw err;
            }
            this._isLoading = false;
            this._dataSource = null;
        }
    };

    cancel = () => {
        if (this._dataSource) {
            this._dataSource.cancel('Cancelled');
        }

        return this;
    };

    onLoad = (cb) => {
        this._onLoad = cb;
        return this;
    };

    onProgress = (cb) => {
        this._onProgress = cb;
        return this;
    };

    static _objects = {};

    static create(key, loadable) {
        return (PrefetchObject._objects[key] =
            PrefetchObject._objects[key] || new PrefetchObject(key, loadable));
    }

    static remove(key) {
        if (PrefetchObject._objects[key]) {
            PrefetchObject._objects[key].cancel();
            PrefetchObject._objects[key]._onLoad = null;
            PrefetchObject._objects[key]._onProgress = null;
            PrefetchObject._objects[key] = undefined;
            delete PrefetchObject._objects[key];
        }
    }

    static cancelAll() {
        Object.keys(PrefetchObject._objects).forEach((key) => {
            PrefetchObject._objects[key].cancel();
        });
    }

    static cancelAllWithout(val) {
        Object.keys(PrefetchObject._objects).forEach((key) => {
            if (val !== key) {
                PrefetchObject._objects[key].cancel();
            }
        });
    }
}
